import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import classNames from 'classnames/bind';
import commaNumber from 'comma-number';
import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';

import styles from './Monitor.module.scss';

import { Cell, Row } from '@/components/ui/dashboard/Monitor';
import { FC_STATUS, GPS_FIX_TYPE } from '@/define/robotStatus';
import { getDistance } from '@/utils/MapUtils';

const cx = classNames.bind(styles);

const Monitor = ({ robotId }) => {
  const robotParams = useSelector((state) => state.robotParams[robotId]);
  const heartbeat = useSelector((state) => state.telemetry[robotId]?.heartbeat);
  const globalPosition = useSelector((state) => state.telemetry[robotId]?.globalPosition);
  const gpsRaw = useSelector((state) => state.telemetry[robotId]?.gpsRaw);
  const vfrHud = useSelector((state) => state.telemetry[robotId]?.vfrHud);
  const homePosition = useSelector((state) => state.telemetry[robotId]?.homePosition);
  const [manualControl, setManualControl] = useState(false);

  useUpdateEffect(() => {
    const { lastParamId } = robotParams;
    if (lastParamId !== 'COM_CTRL_AUTH') return;

    setManualControl(robotParams.params[lastParamId].value === 1);
  }, [robotParams]);

  // Arm State, FC Status
  const { isArm, armState, fcStatus } = useMemo(() => {
    const isArm = heartbeat?.isArm ?? false;

    return {
      isArm,
      armState: isArm ? 'ARM' : 'DISARM',
      fcStatus: FC_STATUS[heartbeat?.status] ?? '-',
    };
  }, [heartbeat]);

  // Distance to Home
  const distanceToHome = useMemo(() => {
    if (!globalPosition) return 0;
    if (!homePosition) return 0;

    return getDistance(globalPosition, homePosition);
  }, [globalPosition, homePosition]);

  // Speed
  const { groundspeed } = useMemo(() => {
    return {
      groundspeed: vfrHud?.groundspeed ?? 0,
    };
  }, [vfrHud]);

  // #region Total Distance Traveled
  const prevTimeRef = useRef(null);
  const accumulatedDistance = useRef(0);
  const [totalDistance, setTotalDistance] = useState(0);

  useEffect(() => {
    if (!vfrHud) return;

    // DISARM 상태에서는 거리 누적 중단
    if (!isArm) {
      prevTimeRef.current = null;
      return;
    }

    const currentTime = Date.now();

    if (prevTimeRef.current !== null) {
      const timeDiff = (currentTime - prevTimeRef.current) / 1000; // 시간 단위 변환
      const distance = groundspeed * timeDiff; // m/s * s = m

      accumulatedDistance.current += distance;
      setTotalDistance(accumulatedDistance.current);
    }

    prevTimeRef.current = currentTime;
  }, [vfrHud, isArm]);
  // #endregion

  return (
    <div className={cx('container')}>
      <div className={cx('rows')}>
        <Row label="Arm State" value={armState} valueStyle={!isArm && { color: 'red' }} />
        <Row label="FC Status" value={fcStatus} />
        <Row label="GPS Fix Type" value={gpsRaw ? GPS_FIX_TYPE[gpsRaw.fixType].name : '-'} />
      </div>
      <div className={cx('boxes')}>
        <Cell data={[{ title: 'Distance to Home', value: commaNumber(distanceToHome.toFixed(2)), unit: 'm' }]} />
        <Cell data={[{ title: 'Total Distance', value: commaNumber(totalDistance.toFixed(2)), unit: 'm' }]} />
      </div>
      <div className={cx('boxes')}>
        <Cell data={[{ title: 'Speed', value: commaNumber(groundspeed.toFixed(2)), unit: 'm/s' }]} />
        <Cell data={[{ title: 'Manual Control', value: manualControl ? 'ON' : 'OFF' }]} />
      </div>
    </div>
  );
};

export default Monitor;
